import { AxiosResponse } from 'axios';
import request from './utils';
import * as ROUTES from 'src/api/routes';

export interface SurveyMigrationConfig {
    workspaceId: string;
    apiKey: string;
    maskedApiKey?: string;
    surveyTemplateVersionId?: string;
    isValidated: boolean;
    migrationStatus?: string | null;
}

export interface SurveyMigrationPreview {
    totalSurveys: number;
    stateCounts: Record<string, number>;
    dateRange: { earliest: string; latest: string };
    facilityCount: number;
}

export type SurveyMigrationStatus =
    | 'NOT_STARTED'
    | 'IN_PROGRESS'
    | 'PAUSED'
    | 'COMPLETED'
    | 'FAILED';

export interface SurveyMigrationJob {
    migrationJobId: string;
    status: SurveyMigrationStatus;
    totalSurveys: number;
    processedCount: number;
    failedCount: number;
    startedAt: string | null;
    completedAt: string | null;
    percentComplete: number;
}

export interface SurveyMigrationStartResult {
    migrationJobId: string;
    status: 'IN_PROGRESS';
    totalSurveys: number;
}

export interface SurveyMigrationResumeResult {
    status: 'IN_PROGRESS';
    requeuedCount: number;
}

export async function getConfig(): Promise<SurveyMigrationConfig | null> {
    const response = await request({ ...ROUTES.GET_SURVEY_MIGRATION_CONFIG });
    const data = response.data.result ?? response.data;
    if (!data?.workspaceId) return null;
    return {
        workspaceId: data.workspaceId,
        apiKey: data.maskedApiKey ?? data.apiKey ?? '',
        maskedApiKey: data.maskedApiKey,
        surveyTemplateVersionId: data.surveyTemplateVersionId ?? '',
        isValidated: data.isValidated ?? false,
        migrationStatus: data.migrationStatus ?? null,
    };
}

export async function saveConfig(config: SurveyMigrationConfig): Promise<SurveyMigrationConfig> {
    const response = await request({ ...ROUTES.SAVE_SURVEY_MIGRATION_CONFIG, data: config });
    return response.data.result ?? response.data;
}

export async function validateConnection(config: SurveyMigrationConfig): Promise<boolean> {
    const response = await request({ ...ROUTES.VALIDATE_SURVEY_MIGRATION_CONNECTION, data: config });
    const data = response.data.result ?? response.data;
    return data?.valid ?? false;
}

export async function getPreview(): Promise<SurveyMigrationPreview | null> {
    const response = await request({ ...ROUTES.GET_SURVEY_MIGRATION_PREVIEW });
    return response.data.result ?? response.data ?? null;
}

export async function startMigration(): Promise<SurveyMigrationStartResult> {
    const response = await request({ ...ROUTES.START_SURVEY_MIGRATION });
    return response.data.result ?? response.data;
}

export async function getStatus(): Promise<SurveyMigrationJob> {
    const response = await request({ ...ROUTES.GET_SURVEY_MIGRATION_STATUS });
    return response.data.result ?? response.data;
}

export async function pauseMigration(): Promise<void> {
    await request({ ...ROUTES.PAUSE_SURVEY_MIGRATION });
}

export async function resumeMigration(): Promise<SurveyMigrationResumeResult> {
    const response = await request({ ...ROUTES.RESUME_SURVEY_MIGRATION });
    return response.data.result ?? response.data;
}

export async function downloadErrors(): Promise<AxiosResponse> {
    return await request({
        ...ROUTES.DOWNLOAD_SURVEY_MIGRATION_ERRORS,
        responseType: 'blob',
    });
}
